import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  MessageSquare,
  Hash,
  CheckCircle2,
  XCircle,
  RefreshCw,
  FolderKanban,
  Users,
  Cog,
  Sparkles,
  Layout,
  Server,
  FileJson,
  Wrench,
  Zap,
  Brain,
  Search,
  Eye,
  Code,
  Shield,
  Settings,
  Pencil,
  ChevronDown,
  ChevronUp,
  FileCode,
  Info,
  DollarSign,
  Terminal,
  RotateCcw,
  Lock,
  BookOpen,
  ExternalLink,
  Cpu,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';
import { adminService } from '@/services/adminService';
import { useAuthStore } from '@/stores/authStore';
import type {
  LlmConfigsResponse,
  ProjectLlmInfo,
  AgentInfo,
  ToolInfo,
  ModelParametersApi,
  ParameterConstraint,
} from '@/types';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import ModelParameterForm, { BuiltInToolsSection } from '@/components/admin/ModelParameterForm';
import PresetCard from '@/components/admin/PresetCard';
import { AgentDetailSheet } from '@/components/admin/AgentDetailSheet';
import { AgentTopologyGraph } from '@/components/admin/AgentTopologyGraph';
import type { BuiltInToolSettings } from '@/types';

const agentIcons: Record<string, typeof Bot> = {
  orchestrator: Cog,
  oracle: Brain,
  planning: Sparkles,
  frontend: Layout,
  backend: Server,
  mocking: FileJson,
  explorer: Search,
  reviewer: Eye,
  search: Search,
};

const modelPresetColors: Record<string, string> = {
  // Anthropic
  'claude-opus': 'bg-purple-500',
  'claude-sonnet': 'bg-blue-500',
  'claude-haiku': 'bg-green-500',
  // OpenAI
  'gpt-pro': 'bg-emerald-600',
  'gpt-standard': 'bg-emerald-500',
  'gpt-chat': 'bg-teal-500',
  'gpt-mini': 'bg-cyan-500',
  // Google Gemini
  'gemini-pro': 'bg-red-500',
  'gemini-flash': 'bg-orange-500',
};

// Alias for backward compatibility
const modelTierColors = modelPresetColors;

const modelPresetLabels: Record<string, string> = {
  // Anthropic
  'claude-opus': 'Claude Opus',
  'claude-sonnet': 'Claude Sonnet',
  'claude-haiku': 'Claude Haiku',
  // OpenAI
  'gpt-pro': 'GPT Pro',
  'gpt-standard': 'GPT Standard',
  'gpt-chat': 'GPT Chat',
  'gpt-mini': 'GPT Mini',
  // Google Gemini
  'gemini-pro': 'Gemini Pro',
  'gemini-flash': 'Gemini Flash',
};

// Alias for backward compatibility
const modelTierLabels = modelPresetLabels;

export default function LlmConfigsPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const { toast } = useToast();

  const [data, setData] = useState<LlmConfigsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('services');

  // Modal states
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isModelTierModalOpen, setIsModelTierModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedModelTier, setSelectedModelTier] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form states
  const [serviceForm, setServiceForm] = useState({
    type: 'single_agent' as 'single_agent' | 'multi_agent',
    default_agent: '',
    default_preset: '',
  });
  const [modelTierForm, setModelTierForm] = useState({
    provider: '',
    model: '',
  });
  const [modelTierParams, setModelTierParams] = useState<ModelParametersApi>({});
  const [modelTierBuiltinTools, setModelTierBuiltinTools] = useState<Record<string, BuiltInToolSettings>>({});
  const [parameterConstraints, setParameterConstraints] = useState<
    Record<string, Record<string, ParameterConstraint>>
  >({});
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  // Agent detail sheet states
  const [selectedAgent, setSelectedAgent] = useState<AgentInfo | null>(null);
  const [isAgentDetailOpen, setIsAgentDetailOpen] = useState(false);

  // Check if current user is admin
  useEffect(() => {
    if (currentUser && currentUser.role !== 'admin') {
      toast({
        title: '접근 권한 없음',
        description: '관리자만 접근할 수 있습니다.',
        variant: 'destructive',
      });
      navigate('/dashboard');
    }
  }, [currentUser, navigate, toast]);

  const loadData = useCallback(async () => {
    try {
      const [result, constraints] = await Promise.all([
        adminService.getLlmConfigs(),
        adminService.getParameterConstraints().catch(() => ({})),
      ]);
      setData(result);
      setParameterConstraints(constraints);
    } catch (error) {
      toast({
        title: 'LLM 설정 로드 실패',
        description: 'LLM 설정 정보를 불러올 수 없습니다.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadData();
  };

  // Helper to get available models for a provider
  const getAvailableModelsForProvider = (providerName: string) => {
    if (!data?.providers?.[providerName]) return [];
    return data.providers[providerName].availableModels || [];
  };

  // Modal handlers
  const openServiceModal = (serviceName: string) => {
    if (data?.serviceConfigs?.[serviceName]) {
      const config = data.serviceConfigs[serviceName];
      setServiceForm({
        type: config.type,
        default_agent: config.defaultAgent ?? '',
        default_preset: config.defaultPreset ?? '',
      });
      setSelectedService(serviceName);
      setIsServiceModalOpen(true);
    }
  };

  const openModelTierModal = (tierKey: string) => {
    if (data?.modelTiers?.[tierKey]) {
      const tier = data.modelTiers[tierKey];
      setModelTierForm({
        provider: tier.provider,
        model: tier.model,
      });
      // 파라미터 초기화 (현재 값 또는 기본값)
      setModelTierParams(tier.parameters || tier.defaultParameters || {});
      // 내장 도구 설정 초기화
      setModelTierBuiltinTools(tier.builtinTools || tier.defaultBuiltinTools || {});
      setSelectedModelTier(tierKey);
      setIsModelTierModalOpen(true);
    }
  };

  // 파라미터 제약사항 가져오기
  const getConstraintsForProvider = (provider: string): Record<string, ParameterConstraint> => {
    const common = parameterConstraints.common || {};
    const providerSpecific = parameterConstraints[provider] || {};
    return { ...common, ...providerSpecific };
  };

  // 모델별 파라미터 제약사항 가져오기
  const getRestrictionsForModel = (tierKey: string): Record<string, boolean> => {
    if (!data?.modelTiers?.[tierKey]) return {};
    return data.modelTiers[tierKey].parameterRestrictions || {};
  };

  // 파라미터 기본값으로 리셋
  const handleResetParameters = () => {
    if (selectedModelTier && data?.modelTiers?.[selectedModelTier]) {
      const defaultParams = data.modelTiers[selectedModelTier].defaultParameters || {};
      setModelTierParams(defaultParams);
    }
  };

  const handleSaveService = async () => {
    if (!selectedService) return;
    setIsSaving(true);
    try {
      await adminService.updateServiceConfig(selectedService, {
        type: serviceForm.type,
        default_agent: serviceForm.default_agent || undefined,
        default_preset: serviceForm.default_preset || undefined,
      });
      toast({
        title: '설정 저장 완료',
        description: '서비스 설정이 저장되었습니다.',
      });
      setIsServiceModalOpen(false);
      loadData();
    } catch {
      toast({
        title: '설정 저장 실패',
        description: '서비스 설정을 저장할 수 없습니다.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveModelTier = async () => {
    if (!selectedModelTier) return;
    setIsSaving(true);
    try {
      await adminService.updateModelTier(selectedModelTier, {
        provider: modelTierForm.provider,
        model: modelTierForm.model,
        parameters: modelTierParams,
        builtin_tools: modelTierBuiltinTools,
      });
      toast({
        title: '설정 저장 완료',
        description: '모델 프리셋 설정이 저장되었습니다.',
      });
      setIsModelTierModalOpen(false);
      loadData();
    } catch {
      toast({
        title: '설정 저장 실패',
        description: '모델 프리셋 설정을 저장할 수 없습니다.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getProjectStatusBadge = (status: ProjectLlmInfo['status']) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-500">진행중</Badge>;
      case 'on_hold':
        return <Badge variant="secondary">보류</Badge>;
      case 'completed':
        return <Badge variant="outline">완료</Badge>;
      default:
        return null;
    }
  };

  const getModelTierBadge = (tier: string | undefined) => {
    if (!tier) return null;
    const color = modelTierColors[tier] || 'bg-gray-500';
    const label = modelTierLabels[tier] || tier;
    return (
      <Badge className={`${color} text-white`}>
        {label}
      </Badge>
    );
  };

  const getCostTierBadge = (costTier: string | undefined) => {
    if (!costTier) return null;
    const colors: Record<string, string> = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-green-100 text-green-700 border-green-200',
    };
    const labels: Record<string, string> = {
      high: '높음',
      medium: '보통',
      low: '낮음',
    };
    return (
      <Badge variant="outline" className={colors[costTier] || ''}>
        <DollarSign className="h-3 w-3 mr-1" />
        {labels[costTier] || costTier}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Bot className="h-8 w-8" />
            LLM 설정 관리
          </h1>
          <p className="text-muted-foreground mt-1">
            AI 엔진 설정, 협업 에이전트, 도구 관리
          </p>
        </div>
        <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline">
          <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          새로고침
        </Button>
      </div>

      {/* Tabs */}
      {data && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="services" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              서비스 설정
            </TabsTrigger>
            <TabsTrigger value="agents" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              에이전트 설정
            </TabsTrigger>
            <TabsTrigger value="tools" className="flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              도구 관리
            </TabsTrigger>
            <TabsTrigger value="projects" className="flex items-center gap-2">
              <FolderKanban className="h-4 w-4" />
              프로젝트
            </TabsTrigger>
          </TabsList>

          {/* Services Tab */}
          <TabsContent value="services" className="space-y-6">
            {/* Provider Status */}
            {data.providers && Object.keys(data.providers).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="h-5 w-5" />
                    Provider API 설정
                  </CardTitle>
                  <CardDescription>각 LLM Provider의 API Key 설정 상태</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(data.providers).map(([providerName, config]) => (
                      <div key={providerName} className="p-4 border rounded-lg space-y-3">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-lg capitalize">{providerName}</span>
                          {config.apiKeyConfigured ? (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              <CheckCircle2 className="mr-1 h-3 w-3" />
                              API Key 설정됨
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-red-600 border-red-600">
                              <XCircle className="mr-1 h-3 w-3" />
                              API Key 없음
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          사용 가능 모델: {config.availableModels?.length ?? 0}개
                        </div>
                        <div className="text-xs text-muted-foreground">
                          API Key는 .env 환경변수로 설정하세요
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Service Configs */}
            {data.serviceConfigs && Object.keys(data.serviceConfigs).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    서비스별 LLM 설정
                  </CardTitle>
                  <CardDescription>프로젝트룸 채팅과 일반 채팅의 LLM Provider 및 모델 설정</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(data.serviceConfigs).map(([serviceName, config]) => (
                      <div key={serviceName} className="p-4 border rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {serviceName === 'project_room' ? (
                              <FolderKanban className="h-5 w-5 text-primary" />
                            ) : (
                              <MessageSquare className="h-5 w-5 text-primary" />
                            )}
                            <span className="font-semibold">
                              {serviceName === 'project_room' ? '프로젝트룸 채팅' : '일반 채팅'}
                            </span>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => openServiceModal(serviceName)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">{config.description}</p>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">유형:</span>
                            <Badge variant="outline" className="ml-2">
                              {config.type === 'multi_agent' ? 'Multi-Agent' : 'Single LLM'}
                            </Badge>
                          </div>
                          {config.type === 'multi_agent' && config.defaultAgent && (
                            <div>
                              <span className="text-muted-foreground">기본 에이전트:</span>
                              <span className="ml-2 font-medium capitalize">{config.defaultAgent}</span>
                            </div>
                          )}
                          {config.type === 'single_agent' && (
                            <div>
                              <span className="text-muted-foreground">모델 프리셋:</span>
                              {config.defaultPreset ? (
                                <Badge className={`ml-2 ${modelPresetColors[config.defaultPreset] || 'bg-gray-500'} text-white`}>
                                  {modelPresetLabels[config.defaultPreset] || config.defaultPreset}
                                </Badge>
                              ) : (
                                <span className="ml-2 font-medium">-</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Model Presets */}
            {data.modelTiers && Object.keys(data.modelTiers).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5" />
                    모델 프리셋
                  </CardTitle>
                  <CardDescription>
                    에이전트별로 사용되는 사전 정의된 모델 설정. 각 프리셋의 파라미터를 개별 설정할 수 있습니다.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(data.modelTiers).map(([tierKey, tier]) => (
                      <PresetCard
                        key={tierKey}
                        tierKey={tierKey}
                        tier={tier}
                        agents={data.agentSettings || data.availableAgents}
                        onEdit={openModelTierModal}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Official Documentation Links */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  공식 문서 및 SDK
                </CardTitle>
                <CardDescription>
                  각 LLM Provider의 공식 API 문서, SDK, 개발 가이드 링크
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* OpenAI */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-950/50 transition-colors">
                    <ChevronDown className="h-4 w-4" />
                    <span className="font-semibold text-emerald-700 dark:text-emerald-400">OpenAI</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <a href="https://platform.openai.com/docs/api-reference" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        API Reference
                      </a>
                      <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        개발자 문서
                      </a>
                      <a href="https://github.com/openai/openai-python" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        Python SDK
                      </a>
                      <a href="https://github.com/openai/openai-node" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        Node.js SDK
                      </a>
                      <a href="https://cookbook.openai.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        Cookbook
                      </a>
                      <a href="https://openai.com/api/pricing" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        가격 정보
                      </a>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Anthropic */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-950/50 transition-colors">
                    <ChevronDown className="h-4 w-4" />
                    <span className="font-semibold text-purple-700 dark:text-purple-400">Anthropic (Claude)</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <a href="https://docs.anthropic.com/en/api" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        API Reference
                      </a>
                      <a href="https://docs.anthropic.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        개발자 문서
                      </a>
                      <a href="https://github.com/anthropics/anthropic-sdk-python" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        Python SDK
                      </a>
                      <a href="https://github.com/anthropics/anthropic-sdk-typescript" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        TypeScript SDK
                      </a>
                      <a href="https://github.com/anthropics/anthropic-cookbook" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        Cookbook
                      </a>
                      <a href="https://www.anthropic.com/pricing" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        가격 정보
                      </a>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Google */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 bg-red-50 dark:bg-red-950/30 rounded-lg hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors">
                    <ChevronDown className="h-4 w-4" />
                    <span className="font-semibold text-red-700 dark:text-red-400">Google (Gemini)</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <a href="https://ai.google.dev/api" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        API Reference
                      </a>
                      <a href="https://ai.google.dev/gemini-api/docs" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        개발자 문서
                      </a>
                      <a href="https://github.com/googleapis/python-genai" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        Python SDK
                      </a>
                      <a href="https://github.com/google-gemini/generative-ai-js" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        Node.js SDK
                      </a>
                      <a href="https://github.com/google-gemini/gemma-cookbook" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        Cookbook
                      </a>
                      <a href="https://ai.google.dev/gemini-api/docs/pricing" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        가격 정보
                      </a>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>

            {/* Model Specifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cpu className="h-5 w-5" />
                  모델별 상세 정보
                </CardTitle>
                <CardDescription>
                  각 Provider별 모델의 컨텍스트 윈도우, 가격, 특징 정보
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="openai" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="openai" className="text-emerald-600 data-[state=active]:bg-emerald-50 dark:data-[state=active]:bg-emerald-950/30">OpenAI</TabsTrigger>
                    <TabsTrigger value="anthropic" className="text-purple-600 data-[state=active]:bg-purple-50 dark:data-[state=active]:bg-purple-950/30">Anthropic</TabsTrigger>
                    <TabsTrigger value="google" className="text-red-600 data-[state=active]:bg-red-50 dark:data-[state=active]:bg-red-950/30">Google</TabsTrigger>
                  </TabsList>

                  {/* OpenAI Models */}
                  <TabsContent value="openai" className="mt-4">
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-emerald-50/50 dark:bg-emerald-950/20">
                            <TableHead className="font-semibold">모델</TableHead>
                            <TableHead className="text-center">컨텍스트</TableHead>
                            <TableHead className="text-center">Input</TableHead>
                            <TableHead className="text-center">Output</TableHead>
                            <TableHead>특징</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-medium">GPT-5.2 Pro</TableCell>
                            <TableCell className="text-center font-mono text-sm">256K</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-sm text-muted-foreground">최고 성능, 복잡한 추론</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">GPT-5.2</TableCell>
                            <TableCell className="text-center font-mono text-sm">256K</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-sm text-muted-foreground">플래그십 추론 모델</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">GPT-5</TableCell>
                            <TableCell className="text-center font-mono text-sm">128K</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-sm text-muted-foreground">고성능 범용 모델</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">GPT-5-mini</TableCell>
                            <TableCell className="text-center font-mono text-sm">128K</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-sm text-muted-foreground">중간 성능, 비용 효율</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">GPT-5-nano</TableCell>
                            <TableCell className="text-center font-mono text-sm">128K</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-sm text-muted-foreground">빠른 속도, 요약/분류 최적화</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">GPT-5.1-Codex</TableCell>
                            <TableCell className="text-center font-mono text-sm">200K</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-sm text-muted-foreground">에이전틱 코딩 전용</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">GPT-4.1</TableCell>
                            <TableCell className="text-center font-mono text-sm">1M</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-center text-muted-foreground">-</TableCell>
                            <TableCell className="text-sm text-muted-foreground">이전 세대, 1M 컨텍스트</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>

                  {/* Anthropic Models */}
                  <TabsContent value="anthropic" className="mt-4 space-y-4">
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-purple-50/50 dark:bg-purple-950/20">
                            <TableHead className="font-semibold">모델</TableHead>
                            <TableHead className="text-center">컨텍스트</TableHead>
                            <TableHead className="text-center">Input ($/1M)</TableHead>
                            <TableHead className="text-center">Output ($/1M)</TableHead>
                            <TableHead>특징</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-medium">Claude Opus 4.5</TableCell>
                            <TableCell className="text-center font-mono text-sm">200K</TableCell>
                            <TableCell className="text-center font-mono text-sm">$5</TableCell>
                            <TableCell className="text-center font-mono text-sm">$25</TableCell>
                            <TableCell className="text-sm text-muted-foreground">최고 성능, 복잡한 워크플로우</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Claude Sonnet 4.5</TableCell>
                            <TableCell className="text-center font-mono text-sm">200K~1M</TableCell>
                            <TableCell className="text-center font-mono text-sm">$3</TableCell>
                            <TableCell className="text-center font-mono text-sm">$15</TableCell>
                            <TableCell className="text-sm text-muted-foreground">균형잡힌 성능, 코딩/에이전트 권장</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Claude Haiku 4.5</TableCell>
                            <TableCell className="text-center font-mono text-sm">200K</TableCell>
                            <TableCell className="text-center font-mono text-sm">$1</TableCell>
                            <TableCell className="text-center font-mono text-sm">$5</TableCell>
                            <TableCell className="text-sm text-muted-foreground">가장 빠름, 비용 효율</TableCell>
                          </TableRow>
                          <TableRow className="bg-muted/30">
                            <TableCell className="font-medium text-muted-foreground">Claude Opus 4.1 (Legacy)</TableCell>
                            <TableCell className="text-center font-mono text-sm text-muted-foreground">200K</TableCell>
                            <TableCell className="text-center font-mono text-sm text-muted-foreground">$15</TableCell>
                            <TableCell className="text-center font-mono text-sm text-muted-foreground">$75</TableCell>
                            <TableCell className="text-sm text-muted-foreground">레거시 모델</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                    <div className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg text-sm space-y-1">
                      <div className="font-medium text-purple-700 dark:text-purple-400">💡 비용 절감 기능</div>
                      <ul className="text-muted-foreground space-y-0.5 ml-4">
                        <li>• Prompt Caching: 반복 컨텍스트 90% 절감</li>
                        <li>• Batch API: 50% 할인</li>
                        <li>• Extended Thinking: 출력 토큰으로 청구</li>
                      </ul>
                    </div>
                  </TabsContent>

                  {/* Google Models */}
                  <TabsContent value="google" className="mt-4 space-y-4">
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-red-50/50 dark:bg-red-950/20">
                            <TableHead className="font-semibold">모델</TableHead>
                            <TableHead className="text-center">컨텍스트</TableHead>
                            <TableHead className="text-center">Input ($/1M)</TableHead>
                            <TableHead className="text-center">Output ($/1M)</TableHead>
                            <TableHead>특징</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-medium">Gemini 3 Pro (Preview)</TableCell>
                            <TableCell className="text-center font-mono text-sm">1M</TableCell>
                            <TableCell className="text-center font-mono text-sm">$2~4</TableCell>
                            <TableCell className="text-center font-mono text-sm">$12~18</TableCell>
                            <TableCell className="text-sm text-muted-foreground">추론 최적화, 에이전틱 워크플로우</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Gemini 3 Flash (Preview)</TableCell>
                            <TableCell className="text-center font-mono text-sm">200K</TableCell>
                            <TableCell className="text-center font-mono text-sm">$0.50</TableCell>
                            <TableCell className="text-center font-mono text-sm">$3</TableCell>
                            <TableCell className="text-sm text-muted-foreground">빠른 속도, 무료 티어 제공</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Gemini 2.5 Pro</TableCell>
                            <TableCell className="text-center font-mono text-sm">1M</TableCell>
                            <TableCell className="text-center font-mono text-sm">$1.25</TableCell>
                            <TableCell className="text-center font-mono text-sm">$10</TableCell>
                            <TableCell className="text-sm text-muted-foreground">복잡한 추론/코딩</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Gemini 2.5 Flash</TableCell>
                            <TableCell className="text-center font-mono text-sm">1M</TableCell>
                            <TableCell className="text-center font-mono text-sm">$0.10</TableCell>
                            <TableCell className="text-center font-mono text-sm">$0.40</TableCell>
                            <TableCell className="text-sm text-muted-foreground">균형잡힌 멀티모달</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Gemini 2.5 Flash-Lite</TableCell>
                            <TableCell className="text-center font-mono text-sm">1M</TableCell>
                            <TableCell className="text-center font-mono text-sm">$0.10</TableCell>
                            <TableCell className="text-center font-mono text-sm">$0.40</TableCell>
                            <TableCell className="text-sm text-muted-foreground">대규모 처리 최적화</TableCell>
                          </TableRow>
                          <TableRow className="bg-muted/30">
                            <TableCell className="font-medium text-muted-foreground">Gemini 1.5 Pro (Legacy)</TableCell>
                            <TableCell className="text-center font-mono text-sm text-muted-foreground">2M</TableCell>
                            <TableCell className="text-center font-mono text-sm text-muted-foreground">$0.075</TableCell>
                            <TableCell className="text-center font-mono text-sm text-muted-foreground">$0.30</TableCell>
                            <TableCell className="text-sm text-muted-foreground">레거시, 최대 컨텍스트</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                    <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg text-sm space-y-1">
                      <div className="font-medium text-red-700 dark:text-red-400">💡 비용 절감 기능</div>
                      <ul className="text-muted-foreground space-y-0.5 ml-4">
                        <li>• Batch Processing: 50% 할인 (24시간 내 결과)</li>
                        <li>• Context Caching: 입력 토큰의 ~25% 비용</li>
                        <li>• 무료 티어: 신용카드 없이 모든 모델 테스트 가능</li>
                      </ul>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agents Tab */}
          <TabsContent value="agents" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  협업 에이전트 설정
                </CardTitle>
                <CardDescription>프로젝트룸에서 활동하는 AI 에이전트와 할당된 모델</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[180px]">에이전트</TableHead>
                        <TableHead className="min-w-[200px]">설명</TableHead>
                        <TableHead className="w-[120px] text-center">모델 프리셋</TableHead>
                        <TableHead className="w-[200px]">실제 모델</TableHead>
                        <TableHead className="w-[80px] text-center">비용</TableHead>
                        <TableHead className="min-w-[150px]">기능 / 도구</TableHead>
                        <TableHead className="w-[80px] text-center">상세</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data.agentSettings || data.availableAgents).map((agent: AgentInfo) => {
                        const IconComponent = agentIcons[agent.type] || Bot;
                        return (
                          <TableRow key={agent.type} className="hover:bg-muted/50">
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="p-2 rounded-lg bg-primary/10">
                                  <IconComponent className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                  <div className="font-medium">
                                    {(agent.nameKo || agent.name).replace(' Agent', '')}
                                  </div>
                                  <div className="text-xs text-muted-foreground">{agent.type}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {agent.descriptionKo || agent.description}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              {getModelTierBadge(agent.modelTier)}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <span className="font-mono text-xs">{agent.actualModel || '-'}</span>
                                {agent.provider && (
                                  <div className="text-xs text-muted-foreground capitalize">
                                    {agent.provider}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {getCostTierBadge(agent.costTier)}
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1.5">
                                {/* Capabilities */}
                                {agent.capabilities && agent.capabilities.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {agent.capabilities.slice(0, 2).map((cap) => (
                                      <Badge key={cap} variant="secondary" className="text-xs">
                                        {cap.replace(/_/g, ' ')}
                                      </Badge>
                                    ))}
                                    {agent.capabilities.length > 2 && (
                                      <Badge variant="secondary" className="text-xs">
                                        +{agent.capabilities.length - 2}
                                      </Badge>
                                    )}
                                  </div>
                                )}
                                {/* Tools */}
                                {agent.toolsEnabled && agent.toolsEnabled.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {agent.toolsEnabled.slice(0, 2).map((tool) => (
                                      <Badge key={tool} variant="outline" className="text-xs">
                                        {tool}
                                      </Badge>
                                    ))}
                                    {agent.toolsEnabled.length > 2 && (
                                      <Badge variant="outline" className="text-xs">
                                        +{agent.toolsEnabled.length - 2}
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedAgent(agent);
                                  setIsAgentDetailOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Configuration Guide */}
            <Collapsible open={isGuideOpen} onOpenChange={setIsGuideOpen}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileCode className="h-5 w-5" />
                        <CardTitle className="text-base">설정 변경 가이드</CardTitle>
                      </div>
                      {isGuideOpen ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <CardDescription>
                      에이전트 설정은 파일에서 관리됩니다. 변경 방법을 확인하세요.
                    </CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6 border-t pt-6">
                    {/* Config Info Badge */}
                    {data.configInfo && (
                      <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <Info className="h-5 w-5 text-amber-600" />
                        <div className="text-sm">
                          <span className="font-medium">현재 설정 소스:</span>{' '}
                          <code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded">{data.configInfo.path}</code>
                          {data.configInfo.restartRequired && (
                            <span className="text-amber-600 ml-2">(변경 후 재시작 필요)</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Agent Model Preset Guide */}
                    <div className="space-y-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Bot className="h-4 w-4" />
                        에이전트 모델 프리셋 변경
                      </h4>
                      <div className="bg-muted p-4 rounded-lg space-y-3">
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">파일 위치</div>
                          <code className="text-sm bg-background px-2 py-1 rounded">
                            ai-engine/src/agents/config.py
                          </code>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">수정 예시</div>
                          <pre className="text-xs bg-background p-3 rounded overflow-x-auto">
{`AGENT_CONFIG = {
    "orchestrator": {
        "model_tier": ModelPreset.SONNET,  # OPUS → SONNET
        ...
    },
    "planning": {
        "model_tier": ModelPreset.GPT_STANDARD,  # Anthropic → OpenAI
        ...
    },
}`}
                          </pre>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">사용 가능한 프리셋 (9종)</div>
                          <div className="space-y-2">
                            <div className="text-xs text-muted-foreground">Anthropic:</div>
                            <div className="flex gap-2 flex-wrap">
                              <Badge className="bg-purple-500 text-white">ModelPreset.OPUS</Badge>
                              <Badge className="bg-blue-500 text-white">ModelPreset.SONNET</Badge>
                              <Badge className="bg-green-500 text-white">ModelPreset.HAIKU</Badge>
                            </div>
                            <div className="text-xs text-muted-foreground mt-2">OpenAI:</div>
                            <div className="flex gap-2 flex-wrap">
                              <Badge className="bg-emerald-600 text-white">ModelPreset.GPT_PRO</Badge>
                              <Badge className="bg-emerald-500 text-white">ModelPreset.GPT_STANDARD</Badge>
                              <Badge className="bg-teal-500 text-white">ModelPreset.GPT_CHAT</Badge>
                              <Badge className="bg-cyan-500 text-white">ModelPreset.GPT_MINI</Badge>
                            </div>
                            <div className="text-xs text-muted-foreground mt-2">Google:</div>
                            <div className="flex gap-2 flex-wrap">
                              <Badge className="bg-red-500 text-white">ModelPreset.GEMINI_PRO</Badge>
                              <Badge className="bg-orange-500 text-white">ModelPreset.GEMINI_FLASH</Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Global LLM Settings Guide */}
                    <div className="space-y-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        전역 LLM 설정 변경
                      </h4>
                      <div className="bg-muted p-4 rounded-lg space-y-3">
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">파일 위치</div>
                          <code className="text-sm bg-background px-2 py-1 rounded">
                            ai-engine/.env
                          </code>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">설정 가능 항목</div>
                          <pre className="text-xs bg-background p-3 rounded overflow-x-auto">
{`# LLM 기본 설정
LLM_TEMPERATURE=0.7
LLM_MAX_TOKENS=4096
LLM_TIMEOUT=120

# API 키
ANTHROPIC_API_KEY=sk-ant-...
OPENAI_API_KEY=sk-...
GOOGLE_API_KEY=AI...`}
                          </pre>
                        </div>
                      </div>
                    </div>

                    {/* Apply Changes Guide */}
                    <div className="space-y-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Terminal className="h-4 w-4" />
                        변경사항 적용 방법
                      </h4>
                      <div className="bg-muted p-4 rounded-lg">
                        <ol className="list-decimal list-inside space-y-2 text-sm">
                          <li>설정 파일 수정</li>
                          <li>AI 엔진 재시작:
                            <code className="ml-2 bg-background px-2 py-1 rounded text-xs">
                              docker restart playground_ai_engine
                            </code>
                          </li>
                          <li>이 페이지에서 새로고침하여 변경 확인</li>
                        </ol>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Agent Topology Graph */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cog className="h-5 w-5" />
                  에이전트 토폴로지
                </CardTitle>
                <CardDescription>
                  에이전트 간의 작업 위임 및 협업 관계를 시각화합니다. 노드를 클릭하면 상세 정보를 볼 수 있습니다.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AgentTopologyGraph
                  agents={data.agentSettings || data.availableAgents || []}
                  onAgentClick={(agent) => {
                    setSelectedAgent(agent);
                    setIsAgentDetailOpen(true);
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tools Tab */}
          <TabsContent value="tools" className="space-y-6">
            {data.availableTools && Object.keys(data.availableTools).length > 0 ? (
              Object.entries(data.availableTools).map(([category, tools]) => {
                // Get category icon
                const getCategoryIcon = () => {
                  switch (category) {
                    case 'web': return <Search className="h-5 w-5" />;
                    case 'code': return <Code className="h-5 w-5" />;
                    case 'file': return <FileJson className="h-5 w-5" />;
                    case 'communication': return <MessageSquare className="h-5 w-5" />;
                    case 'infrastructure': return <Server className="h-5 w-5" />;
                    case 'project': return <FolderKanban className="h-5 w-5" />;
                    case 'analysis': return <Eye className="h-5 w-5" />;
                    case 'search': return <Search className="h-5 w-5" />;
                    case 'utility': return <Wrench className="h-5 w-5" />;
                    default: return <Wrench className="h-5 w-5" />;
                  }
                };

                // Get agent icon component
                const getAgentIcon = (agentType: string) => {
                  const IconComponent = agentIcons[agentType] || Bot;
                  return IconComponent;
                };

                // Get agent display name
                const getAgentDisplayName = (agentType: string) => {
                  const agentInfo = (data.agentSettings || data.availableAgents)?.find(
                    (a: AgentInfo) => a.type === agentType
                  );
                  if (agentInfo) {
                    return (agentInfo.nameKo || agentInfo.name).replace(' Agent', '');
                  }
                  return agentType;
                };

                return (
                  <Card key={category}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 capitalize">
                        {getCategoryIcon()}
                        {category} 도구
                        <Badge variant="secondary" className="ml-2">
                          {tools.length}개
                        </Badge>
                      </CardTitle>
                      <CardDescription>
                        {category === 'web' && '웹 검색 및 스크래핑 관련 도구'}
                        {category === 'code' && '코드 분석 및 실행 관련 도구'}
                        {category === 'file' && '파일 읽기/쓰기 및 분석 도구'}
                        {category === 'analysis' && '코드 분석 및 품질 검사 도구'}
                        {category === 'project' && '프로젝트 관리 도구'}
                        {!['web', 'code', 'file', 'analysis', 'project'].includes(category) &&
                          `${tools.length}개의 도구 사용 가능`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {tools.map((tool: ToolInfo) => (
                          <div
                            key={tool.name}
                            className={`p-4 border rounded-lg transition-all hover:shadow-md ${
                              tool.enabled
                                ? 'bg-background border-border'
                                : 'bg-muted/50 opacity-60 border-muted'
                            }`}
                          >
                            {/* Header: Name, Version, Status */}
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-sm">{tool.name}</span>
                                  {tool.version && (
                                    <Badge variant="outline" className="text-xs font-mono">
                                      v{tool.version}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {tool.enabled ? (
                                <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  활성
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground text-xs">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  비활성
                                </Badge>
                              )}
                            </div>

                            {/* Description */}
                            <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                              {tool.description}
                            </p>

                            {/* API Key Requirement */}
                            {tool.requiresApiKey && (
                              <div className="mb-3 flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 px-2 py-1.5 rounded-md">
                                <Shield className="h-3.5 w-3.5" />
                                <span>API Key 필요: <code className="font-mono">{tool.requiresApiKey}</code></span>
                              </div>
                            )}

                            {/* Usable By Agents */}
                            {tool.usableByAgents && tool.usableByAgents.length > 0 && (
                              <div className="pt-3 border-t">
                                <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  사용 가능한 에이전트
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {tool.usableByAgents.map((agentType) => {
                                    const AgentIcon = getAgentIcon(agentType);
                                    return (
                                      <Badge
                                        key={agentType}
                                        variant="secondary"
                                        className="text-xs flex items-center gap-1 px-2 py-0.5"
                                      >
                                        <AgentIcon className="h-3 w-3" />
                                        {getAgentDisplayName(agentType)}
                                      </Badge>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Parameters (Collapsible) */}
                            {tool.parameters && Object.keys(tool.parameters).length > 0 && (
                              <Collapsible className="mt-3 pt-3 border-t">
                                <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors w-full">
                                  <ChevronDown className="h-3 w-3" />
                                  <span>파라미터 스키마 보기</span>
                                </CollapsibleTrigger>
                                <CollapsibleContent className="mt-2">
                                  <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-40">
                                    {JSON.stringify(tool.parameters, null, 2)}
                                  </pre>
                                </CollapsibleContent>
                              </Collapsible>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <Wrench className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>도구 정보를 불러올 수 없습니다.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderKanban className="h-5 w-5" />
                  프로젝트별 LLM 현황
                </CardTitle>
                <CardDescription>
                  총 {data.projects.length}개 프로젝트의 채팅 및 토큰 사용량
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>프로젝트</TableHead>
                        <TableHead>상태</TableHead>
                        <TableHead>소유자</TableHead>
                        <TableHead className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <MessageSquare className="h-4 w-4" />
                            세션
                          </div>
                        </TableHead>
                        <TableHead className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <MessageSquare className="h-4 w-4" />
                            메시지
                          </div>
                        </TableHead>
                        <TableHead className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Hash className="h-4 w-4" />
                            토큰
                          </div>
                        </TableHead>
                        <TableHead className="text-center">에이전트</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.projects.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                            프로젝트가 없습니다
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.projects.map((project: ProjectLlmInfo) => (
                          <TableRow key={project.id}>
                            <TableCell>
                              <div className="font-medium">{project.name}</div>
                            </TableCell>
                            <TableCell>{getProjectStatusBadge(project.status)}</TableCell>
                            <TableCell>{project.ownerName}</TableCell>
                            <TableCell className="text-center">{project.chatSessionCount}</TableCell>
                            <TableCell className="text-center">{formatNumber(project.totalMessages)}</TableCell>
                            <TableCell className="text-center">{formatNumber(project.totalTokens)}</TableCell>
                            <TableCell className="text-center">
                              {project.agentsAvailable ? (
                                <Badge variant="outline" className="text-green-600 border-green-600">
                                  <CheckCircle2 className="mr-1 h-3 w-3" />
                                  사용 가능
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground">
                                  <XCircle className="mr-1 h-3 w-3" />
                                  비활성
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Service Config Modal */}
      <Dialog open={isServiceModalOpen} onOpenChange={setIsServiceModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {selectedService === 'project_room' ? '프로젝트룸 채팅' : '일반 채팅'} 설정
            </DialogTitle>
            <DialogDescription>
              서비스의 LLM Provider와 모델을 설정합니다.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="service_type" className="text-right">
                유형
              </Label>
              <Select
                value={serviceForm.type}
                onValueChange={(value) => setServiceForm({ ...serviceForm, type: value as 'single_agent' | 'multi_agent' })}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="유형 선택" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single_agent">Single LLM</SelectItem>
                  <SelectItem value="multi_agent">Multi-Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {serviceForm.type === 'multi_agent' && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="default_agent" className="text-right">
                  기본 에이전트
                </Label>
                <Select
                  value={serviceForm.default_agent}
                  onValueChange={(value) => setServiceForm({ ...serviceForm, default_agent: value })}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="에이전트 선택" />
                  </SelectTrigger>
                  <SelectContent>
                    {data?.availableAgents.map((agent) => (
                      <SelectItem key={agent.type} value={agent.type}>
                        <div className="flex flex-col">
                          <span>{agent.name}</span>
                          <span className="text-xs text-muted-foreground">{agent.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {serviceForm.type === 'single_agent' && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="default_preset" className="text-right">
                  모델 프리셋
                </Label>
                <Select
                  value={serviceForm.default_preset}
                  onValueChange={(value) => setServiceForm({ ...serviceForm, default_preset: value })}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="프리셋 선택" />
                  </SelectTrigger>
                  <SelectContent>
                    {data?.modelTiers && Object.entries(data.modelTiers).map(([key, tier]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${modelPresetColors[key] || 'bg-gray-500'}`} />
                          <span>{modelPresetLabels[key] || key}</span>
                          <span className="text-xs text-muted-foreground">
                            ({tier.provider} - {tier.model})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsServiceModalOpen(false)}>
              취소
            </Button>
            <Button onClick={handleSaveService} disabled={isSaving}>
              {isSaving ? '저장 중...' : '저장'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Model Preset Modal */}
      <Dialog open={isModelTierModalOpen} onOpenChange={setIsModelTierModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedModelTier && (
                <Badge className={`${modelPresetColors[selectedModelTier] || 'bg-gray-500'} text-white`}>
                  {modelPresetLabels[selectedModelTier] || selectedModelTier}
                </Badge>
              )}
              프리셋 설정
            </DialogTitle>
            <DialogDescription>
              Provider, 모델 및 파라미터를 설정합니다. 변경사항은 즉시 적용됩니다.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">기본 설정</TabsTrigger>
              <TabsTrigger value="parameters">파라미터</TabsTrigger>
              <TabsTrigger value="tools">내장 도구</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 pt-4">
              {/* 프리셋 고정 안내 */}
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                <Lock className="h-4 w-4" />
                <span>프리셋의 프로바이더와 모델은 고정되어 있습니다. 파라미터와 내장 도구만 설정할 수 있습니다.</span>
              </div>

              <div className="grid gap-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="tier_provider" className="text-right flex items-center gap-1.5 justify-end">
                    <Lock className="h-3 w-3 text-muted-foreground" />
                    Provider
                  </Label>
                  <Select
                    value={modelTierForm.provider}
                    disabled={true}
                  >
                    <SelectTrigger className="col-span-3 bg-muted/30 cursor-not-allowed">
                      <SelectValue placeholder="Provider 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="anthropic">Anthropic</SelectItem>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="google">Google</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="tier_model" className="text-right flex items-center gap-1.5 justify-end">
                    <Lock className="h-3 w-3 text-muted-foreground" />
                    모델
                  </Label>
                  <Select
                    value={modelTierForm.model}
                    disabled={true}
                  >
                    <SelectTrigger className="col-span-3 bg-muted/30 cursor-not-allowed">
                      <SelectValue placeholder="모델 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableModelsForProvider(modelTierForm.provider).map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          <div className="flex flex-col">
                            <span>{model.name}</span>
                            <span className="text-xs text-muted-foreground">{model.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* 현재 설정 정보 */}
              {selectedModelTier && data?.modelTiers?.[selectedModelTier] && (
                <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                  <div className="text-sm font-medium">현재 설정 정보</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">비용 등급:</span>
                      <span className="ml-2">{data.modelTiers[selectedModelTier].costTier || '-'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">설명:</span>
                      <span className="ml-2">
                        {data.modelTiers[selectedModelTier].descriptionKo ||
                          data.modelTiers[selectedModelTier].description}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="parameters" className="pt-4">
              {modelTierForm.provider ? (
                <ModelParameterForm
                  provider={modelTierForm.provider}
                  model={modelTierForm.model}
                  parameters={modelTierParams}
                  constraints={getConstraintsForProvider(modelTierForm.provider)}
                  restrictions={selectedModelTier ? getRestrictionsForModel(selectedModelTier) : {}}
                  onChange={setModelTierParams}
                  onReset={handleResetParameters}
                />
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Info className="h-8 w-8 mx-auto mb-2" />
                  <p>Provider를 먼저 선택해주세요.</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="tools" className="pt-4">
              {modelTierForm.provider && selectedModelTier && data?.modelTiers?.[selectedModelTier]?.availableBuiltinTools ? (
                <BuiltInToolsSection
                  provider={modelTierForm.provider}
                  availableTools={data.modelTiers[selectedModelTier].availableBuiltinTools || {}}
                  toolSettings={modelTierBuiltinTools}
                  onChange={setModelTierBuiltinTools}
                />
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Info className="h-8 w-8 mx-auto mb-2" />
                  <p>{!modelTierForm.provider ? 'Provider를 먼저 선택해주세요.' : '이 프로바이더에는 사용 가능한 내장 도구가 없습니다.'}</p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <Button variant="ghost" onClick={handleResetParameters} disabled={isSaving}>
              <RotateCcw className="h-4 w-4 mr-2" />
              기본값 복원
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsModelTierModalOpen(false)}>
                취소
              </Button>
              <Button onClick={handleSaveModelTier} disabled={isSaving}>
                {isSaving ? '저장 중...' : '저장'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Agent Detail Sheet */}
      <AgentDetailSheet
        agent={selectedAgent}
        open={isAgentDetailOpen}
        onOpenChange={setIsAgentDetailOpen}
      />
    </div>
  );
}
